import React, { useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, X, Menu } from 'react-feather';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import TextField from 'ui/TextField';
import { Text, TEXT_VARIANTS } from 'ui/Typography';
import IconButton from 'ui/IconButton';
import { DIRECTION, SORTING } from 'constants/config';
import { CustomDragLayer } from './CustomDragLayer';

export const ItemTypes = {
  CHOICE: 'choice',
};

const Choice = ({
  choice,
  onUpdate,
  onDelete,
  addChoice,
  reorder,
  loading,
  disableUpArrow,
  disableDownArrow,
  disableAddItem,
  index,
  moveChoice,
  enableDrag,
  onDrop,
  hideAllGrabbers,
  className,
}) => {
  const inputRef = useRef();

  const ref = useRef(null);
  useEffect(() => {
    if (inputRef.current && !loading && choice.text.length === 0)
      inputRef.current.focus();
  }, [inputRef, loading, choice.text]);

  const { id, position } = choice;

  const [{ handlerId }, drop] = useDrop({
    accept: ItemTypes.CHOICE,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      // Time to actually perform the action
      moveChoice(dragIndex, hoverIndex);
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      // props.order(index, hoverIndex);
      monitor.getItem().index = hoverIndex;
      // item.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemTypes.CHOICE,
    item: () => {
      return { id, index, ...choice };
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
    end: item => {
      const { id: droppedId } = item;
      onDrop(droppedId);
    },
  });

  useEffect(() => {
    // Uses a transparent empty Image to hide the browser-drawn drag preview completely
    preview(getEmptyImage());
  }, [preview]);

  const opacity = isDragging ? 0 : 1;

  drop(ref);

  return (
    <div
      ref={ref}
      style={{ opacity }}
      className={`d-f fd-r jc-sb ai-c SortingChoice ${className}`}
      data-handler-id={handlerId}
    >
      <CustomDragLayer />
      <div
        ref={hideAllGrabbers ? null : drag}
        style={{ opacity: hideAllGrabbers ? 0 : 1 }}
        className="d-f ai-c"
      >
        <IconButton
          className="SortingChoice-grabber"
          type="button"
          aria-label={`${t('common.move_choice')} ${position}`}
          disabled={hideAllGrabbers}
        >
          <Menu size={21} />
        </IconButton>
      </div>
      <div className="minw-25 mr-8">
        <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD_DARK}>{position}.</Text>
      </div>
      <div className="w-85p mb-8 mr-16">
        <TextField
          whiteBackground
          disabled={loading}
          maxLength={SORTING.MAX_CHARACTERS}
          ref={inputRef}
          placeholder={t('questions.choices.sorting.placeholder')}
          value={choice.text}
          name={`SortingChoice-${id}`}
          onChange={e =>
            onUpdate({
              ...choice,
              text: e.target.value,
            })
          }
          aria-label={`${t(
            'questions.choices.sorting.items_label',
          )} ${position}`}
          onKeyDown={e => {
            if (e.key === 'Enter' && !disableAddItem) {
              addChoice();
            }
          }}
        />
      </div>
      <div className="d-f fd-r ai-c">
        <div>
          <IconButton
            tooltip={t('common.move_up')}
            type="button"
            onClick={() => reorder(DIRECTION.UP)}
            disabled={disableUpArrow || loading}
            className="SortingChoice-arrows SortingChoice-arrows-up"
            aria-label={`${t('common.move_up')} ${position}`}
          >
            <ChevronUp size={24} />
          </IconButton>
          <IconButton
            tooltip={t('common.move_down')}
            type="button"
            onClick={() => reorder(DIRECTION.DOWN)}
            disabled={disableDownArrow || loading}
            className="SortingChoice-arrows SortingChoice-arrows-down"
            aria-label={`${t('common.move_down')} ${position}`}
          >
            <ChevronDown size={24} />
          </IconButton>
        </div>
        <IconButton
          tooltip={t('common.remove')}
          type="button"
          onClick={() => onDelete(choice)}
          disabled={loading}
          aria-label={`${t('common.remove')} ${position}`}
        >
          <X size={32} />
        </IconButton>
      </div>
    </div>
  );
};

export default Choice;
